// The "Show sources" display mode: filters SOURCE LISTS (verse commentary
// sections, rabbit-hole cite lists, bundles, the Explore Next rail) by which
// language the learner can actually READ, and decides which title (Hebrew /
// English / both) a card displays. This is a SEPARATE concept from the
// reader's "Translation: on/off" toggle, which controls the opened TEXT - do
// not merge them.
//
// Accuracy posture (ACCURACY-LOCK):
//   - "English only" means the learner can actually open this source in
//     English - it keys off Sefaria's per-link `sourceHasEn` boolean (real
//     text availability), never off the title language.
//   - UNKNOWN NEVER HIDES. When availability was not validated this request
//     (hasEn/hasHe is null/undefined), the source stays visible and is
//     excluded from "x of y" hidden counts. We only hide on a validated
//     `false` (or a curated override).
//   - Precedence for English: manual override -> sourceHasEn -> unknown.
//     The override table exists because sourceHasEn under-reports a small
//     tail (~2% measured live 2026-06-09: e.g. Onkelos has English on
//     Sefaria but sourceHasEn=false). Only LIVE-VERIFIED entries may be
//     added: each key was checked against /api/texts returning real English.
//   - Hebrew has no provider availability field (verified live: links carry
//     no `sourceHasHe`); the corpus is Hebrew-first and getText fail-louds
//     without Hebrew. So hasHebrewAvailable returns null today and the
//     hebrew-mode hide rule is wired but inert until data ever says false.

import { norm } from "./taxonomy.ts";

export type SourceDisplayMode = "default" | "english" | "hebrew";

export function isSourceDisplayMode(v: unknown): v is SourceDisplayMode {
  return v === "default" || v === "english" || v === "hebrew";
}

// ---- mode state (in-memory; persisted by store.ts Prefs, seeded by app.ts) --

let mode: SourceDisplayMode = "default";

export function getSourceLangMode(): SourceDisplayMode {
  return mode;
}

export function setSourceLangMode(m: SourceDisplayMode): void {
  mode = m;
}

export function _resetSourceLangForTests(): void {
  mode = "default";
}

// ---- the facts a decision needs (structural - SourceLink and Item both fit) --

export interface SourceLangFacts {
  commentator: string;
  commentatorHe: string | null;
  hasEn?: boolean | null;
  hasHe?: boolean | null;
}

// Curated corrections to Sefaria's sourceHasEn (keys normalized via the same
// norm() taxonomy/era matching uses). POLARITY IS ONE-WAY BY TYPE: entries can
// only WIDEN visibility (the `true` value type makes a hide-entry a compile
// error). Hiding must always come from data validated THIS request
// (ACCURACY-LOCK) - a curated "this has no English" does not belong here.
// RULE: an entry may be added ONLY after verifying against the live Sefaria
// texts API that English actually exists. Verified 2026-06-09/10 against live
// /api/texts (Torah coverage - this app anchors on parasha verses). Keys must
// match the LINK's collectiveTitle.en as normalized - Onkelos links carry
// per-book collective titles ("Onkelos Numbers"), verified live, so each book
// gets its own entry:
//   - Onkelos: English confirmed on Onkelos_{Genesis,Exodus,Leviticus,
//     Numbers,Deuteronomy} 1:1/15:38 probes; links report sourceHasEn=false
//     (under-report).
//   - Kli Yakar (collectiveTitle.en "Kli Yakar"): English confirmed on
//     Kli_Yakar_on_Numbers.15.38.1; same under-report in the links response.
const EN_AVAILABILITY_OVERRIDE: ReadonlyMap<string, true> = new Map([
  ["onkelos", true],
  ["onkelos genesis", true],
  ["onkelos exodus", true],
  ["onkelos leviticus", true],
  ["onkelos numbers", true],
  ["onkelos deuteronomy", true],
  ["kli yakar", true],
]);

// ---- availability ----------------------------------------------------------

// true/false = validated knowledge (the widen-only override first, then this
// request's sourceHasEn); null = unknown (never fetched / field absent) ->
// caller shows. The override can only ever return true (see table) - a false
// can only come from this request's validated boolean.
export function hasEnglishAvailable(s: SourceLangFacts): boolean | null {
  if (EN_AVAILABILITY_OVERRIDE.get(norm(s.commentator)) === true) return true;
  return typeof s.hasEn === "boolean" ? s.hasEn : null;
}

// No provider field exists today (see header) - this stays null unless a
// validated boolean ever lands on the link/item.
export function hasHebrewAvailable(s: SourceLangFacts): boolean | null {
  return typeof s.hasHe === "boolean" ? s.hasHe : null;
}

// ---- filtering -------------------------------------------------------------

// The single hide/show rule every list applies. Unknown (null) never hides.
export function shouldShowSourceForDisplayMode(s: SourceLangFacts, m: SourceDisplayMode): boolean {
  if (m === "english") return hasEnglishAvailable(s) !== false;
  if (m === "hebrew") return hasHebrewAvailable(s) !== false;
  return true; // default: everything
}

// ---- titles ----------------------------------------------------------------

// Which title(s) a card shows in this mode. Hebrew mode falls back to the
// English title when no Hebrew title exists (an unlabeled card would be
// worse); English mode never shows Hebrew; default shows whatever exists.
export function getDisplayTitle(
  s: SourceLangFacts,
  m: SourceDisplayMode,
): { he?: string; en?: string } {
  const he = s.commentatorHe ?? undefined;
  const en = s.commentator;
  if (m === "english") return { en };
  if (m === "hebrew") return he ? { he } : { en };
  return he ? { he, en } : { en };
}
